import { useState } from "react";
import { Workflow, WorkflowRule } from "../types";
import {
  useAddWorkflowRule,
  useDeleteWorkflowRule,
  useUpdateWorkflowRule,
  useWorkflowOptions,
} from "../hooks";
import { moduleLabel } from "../utils";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { useToast } from "#/components/common/ToastProvider";

/**
 * WHAT ARE RULES?
 *
 * Rules answer the question: "Which workflow should handle THIS specific request?"
 *
 * You only need rules when you want DIFFERENT approval chains for different
 * situations. For example:
 *   - Claims < $500  → simple 1-step dept-head approval  (one workflow)
 *   - Claims ≥ $500  → dept-head + MD approval           (another workflow)
 *   - Finance dept   → CFO must also sign off            (another workflow)
 *
 * If everyone follows the same chain regardless, you DON'T need rules —
 * just mark this workflow as "Default" and it will be used for all requests.
 *
 * Role-based routing within a SINGLE workflow (e.g., auto-skip dept-head step
 * if the applicant IS the dept head) is configured in the Approval Steps above,
 * using "Auto skip if applicant is approver" — no rules needed for that.
 */

export function WorkflowRuleBuilder({ workflow }: { workflow: Workflow }) {
  const confirm = useConfirm();
  const toast = useToast();
  const addRule = useAddWorkflowRule();
  const updateRule = useUpdateWorkflowRule();
  const deleteRule = useDeleteWorkflowRule();
  const { data: options } = useWorkflowOptions();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [form, setForm] = useState({
    departmentId: "",
    roleId: "",
    minValue: "",
    maxValue: "",
    priorityOrder: "1",
    active: true,
  });

  function reset() {
    setEditingId(null);
    setShowForm(false);
    setForm({
      departmentId: "",
      roleId: "",
      minValue: "",
      maxValue: "",
      priorityOrder: "1",
      active: true,
    });
  }

  function startEdit(rule: WorkflowRule) {
    setEditingId(rule.id);
    setShowForm(true);
    setForm({
      departmentId: rule.departmentId?.toString() ?? "",
      roleId: rule.roleId?.toString() ?? "",
      minValue: rule.minValue?.toString() ?? "",
      maxValue: rule.maxValue?.toString() ?? "",
      priorityOrder: rule.priorityOrder?.toString() ?? "1",
      active: rule.active,
    });
  }

  function payload() {
    return {
      moduleCode: workflow.moduleCode,
      departmentId: form.departmentId ? Number(form.departmentId) : null,
      roleId: form.roleId ? Number(form.roleId) : null,
      minValue: form.minValue ? Number(form.minValue) : null,
      maxValue: form.maxValue ? Number(form.maxValue) : null,
      priorityOrder: Number(form.priorityOrder),
      active: form.active,
    };
  }

  async function save() {
    const wasPublished = workflow.published;

    if (editingId) {
      await updateRule.mutateAsync({ workflowId: workflow.id, ruleId: editingId, payload: payload() });
    } else {
      await addRule.mutateAsync({ workflowId: workflow.id, payload: payload() });
    }

    if (wasPublished) {
      toast.info("This unpublished the workflow — new requests won't route through it until you publish it again.");
    }

    reset();
  }

  async function remove(ruleId: number) {
    if (!(await confirm("Delete this rule?"))) return;

    const wasPublished = workflow.published;

    await deleteRule.mutateAsync({ workflowId: workflow.id, ruleId });

    if (wasPublished) {
      toast.info("This unpublished the workflow — new requests won't route through it until you publish it again.");
    }
  }

  return (
    <div className="space-y-4">
      {/* Explanation callout */}
      <div className="rounded-2xl border bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Routing Rules</h3>
            <p className="mt-1 text-sm text-slate-500">
              Rules decide <strong>which workflow applies</strong> to a given request based on department,
              role, or amount/value range. They are only needed when you have
              multiple workflows for the same module with different approval chains.
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="shrink-0 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
            >
              + Add Rule
            </button>
          )}
        </div>

        {/* When rules are NOT needed */}
        {!workflow.rules?.length && (
          <div className="mt-4 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-700">
            <strong>Tip:</strong> If this is your only workflow for{" "}
            <strong>{moduleLabel(workflow.moduleCode)}</strong>, you don't need rules — just mark it as{" "}
            <strong>Default</strong> using the toolbar above. The backend will route all{" "}
            {moduleLabel(workflow.moduleCode)} requests through it automatically.
            <br />
            <span className="mt-1 block text-blue-600">
              Role-based routing (e.g. HOD auto-skipped, MD required for large amounts) is handled in{" "}
              <strong>Approval Steps</strong> using "Auto skip if applicant is approver" — not here.
            </span>
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="rounded-2xl border bg-white p-5">
          <h4 className="mb-4 font-semibold text-slate-900">
            {editingId ? "Edit Rule" : "New Routing Rule"}
          </h4>
          <p className="mb-4 text-sm text-slate-500">
            This rule will match requests where the submitter belongs to the selected
            department/role <em>and</em> the request value falls within the range.
            Leave fields blank to match any value.
          </p>

          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Match department">
              <select
                value={form.departmentId}
                onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
                className="input"
              >
                <option value="">— Any department —</option>
                {options?.departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name ?? d.code ?? d.id}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Match role">
              <select
                value={form.roleId}
                onChange={(e) => setForm({ ...form, roleId: e.target.value })}
                className="input"
              >
                <option value="">— Any role —</option>
                {options?.roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name ?? r.code ?? r.id}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Priority (lower = checked first)">
              <input
                type="number"
                value={form.priorityOrder}
                onChange={(e) => setForm({ ...form, priorityOrder: e.target.value })}
                className="input"
                min={1}
              />
            </Field>

            <Field label="Min value (amount / days)">
              <input
                type="number"
                value={form.minValue}
                onChange={(e) => setForm({ ...form, minValue: e.target.value })}
                className="input"
                placeholder="0"
              />
            </Field>

            <Field label="Max value (leave blank = no cap)">
              <input
                type="number"
                value={form.maxValue}
                onChange={(e) => setForm({ ...form, maxValue: e.target.value })}
                className="input"
                placeholder="Optional"
              />
            </Field>

            <label className="flex items-center gap-2 rounded-xl border p-3 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Active
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={save}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
            >
              {editingId ? "Update Rule" : "Save Rule"}
            </button>
            <button onClick={reset} className="rounded-xl border px-4 py-2 text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rules table */}
      {!!workflow.rules?.length && (
        <div className="overflow-x-auto rounded-2xl border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-3">Priority</th>
                <th className="p-3">Department</th>
                <th className="p-3">Role</th>
                <th className="p-3">Value range</th>
                <th className="p-3">Active</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...(workflow.rules ?? [])].sort((a, b) => a.priorityOrder - b.priorityOrder).map((rule) => (
                <tr key={rule.id} className="border-t">
                  <td className="p-3">{rule.priorityOrder}</td>
                  <td className="p-3">{rule.departmentCode ?? <span className="text-slate-400">Any</span>}</td>
                  <td className="p-3">{rule.roleCode ?? <span className="text-slate-400">Any</span>}</td>
                  <td className="p-3">
                    {rule.minValue ?? 0} – {rule.maxValue != null ? rule.maxValue : "∞"}
                  </td>
                  <td className="p-3">{rule.active ? "Yes" : "No"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => startEdit(rule)} className="mr-3 text-blue-600">Edit</button>
                    <button onClick={() => remove(rule.id)} className="text-red-600">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}